/**
 * @fileoverview Modal de criação/edição de contato.
 * Shell alinhado à paleta do modal Gerenciar Etiquetas.
 * Campo "Número de WhatsApp" mantém a lógica crítica completa:
 * sanitize, format display, validação remota com correção automática, debounce,
 * feedback visual (spinner/check/error) e submit disabled enquanto inválido.
 */

import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Collapse from "@material-ui/core/Collapse";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Plus, UserPlus, Pencil, Trash2, X, CheckCircle2, ChevronDown, AlertCircle } from "lucide-react";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
	sanitizePhone,
	sanitizePastedPhone,
	formatPhoneDisplay,
	digitsBeforeCaret,
	caretAfterDigits,
} from "../../utils/phoneInput";

const MODAL_FONT_FAMILY = '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif';

const useStyles = makeStyles((theme) => {
	const dark = theme.palette.type === "dark";
	return {
		root: {
			display: "flex",
			flexWrap: "wrap",
			fontFamily: MODAL_FONT_FAMILY,
			width: "100%",
		},
		dialog: {
			"& .MuiDialog-paper": {
				fontFamily: MODAL_FONT_FAMILY,
				width: "min(720px, calc(100vw - 32px))",
				maxWidth: "calc(100vw - 32px)",
				borderRadius: 24,
				overflow: "hidden",
				background: dark
					? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
					: "#ffffff",
				boxShadow: dark
					? "0 24px 80px rgba(0, 0, 0, 0.45)"
					: "0 24px 60px rgba(15, 23, 42, 0.12)",
				border: dark
					? "1px solid rgba(255, 255, 255, 0.08)"
					: "1px solid rgba(15, 23, 42, 0.08)",
				"& *": { fontFamily: MODAL_FONT_FAMILY },
			},
		},
		header: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: theme.spacing(2.5, 3),
			borderBottom: dark
				? "1px solid rgba(255, 255, 255, 0.06)"
				: "1px solid rgba(15, 23, 42, 0.08)",
		},
		title: {
			fontSize: "1.35rem",
			fontWeight: 700,
			color: dark ? "#f8fafc" : "#111827",
			lineHeight: 1.2,
		},
		close: {
			color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
		},
		body: {
			padding: theme.spacing(3),
			maxHeight: "calc(min(88vh, 720px) - 136px)",
			overflowY: "auto",
			...theme.scrollbarStylesSoft,
		},
		embeddedForm: {
			display: "flex",
			flexDirection: "column",
			minHeight: 0,
			maxWidth: "100%",
			width: "100%",
		},
		content: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2.5),
			width: "100%",
		},
		section: { display: "flex", flexDirection: "column", gap: theme.spacing(1.75), width: "100%" },
		sectionHeader: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), width: "100%" },
		sectionTitle: {
			fontSize: 11,
			fontWeight: 700,
			color: dark ? "rgba(248, 250, 252, 0.6)" : "#6b7280",
			textTransform: "uppercase",
			letterSpacing: "0.14em",
		},
		sectionDivider: {
			flex: 1,
			height: 1,
			borderRadius: 999,
			background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.08)",
		},
		fieldsRow: {
			display: "flex",
			gap: theme.spacing(2),
			alignItems: "stretch",
			width: "100%",
			"& > *": { minWidth: 0 },
			[theme.breakpoints.down("sm")]: { flexDirection: "column" },
		},
		field: {
			flex: "1 1 0",
			marginRight: "0 !important",
			minWidth: 0,
			width: "100%",
		},
		input: {
			"& .MuiInputLabel-root": {
				color: dark ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
				fontSize: "0.98rem",
			},
			"& .MuiInputLabel-outlined": {
				transform: "translate(18px, 15px) scale(1)",
			},
			"& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
				// Lift o suficiente pro label sair da linha da borda; a caixinha
				// de fundo na cor do modal "corta" a borda visualmente (o fieldset
				// notched foi desabilitado com `& fieldset { border: none }`).
				transform: "translate(12px, -10px) scale(0.78)",
				padding: "0 6px",
				background: dark
					? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
					: "#ffffff",
				color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
			},
			"& .MuiOutlinedInput-root": {
				minHeight: 48,
				alignItems: "center",
				padding: theme.spacing(0, 1.8),
				borderRadius: 16,
				border: dark
					? "1px solid rgba(255, 255, 255, 0.12)"
					: "1px solid rgba(15, 23, 42, 0.12)",
				background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)",
				transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
				"& fieldset": { border: "none" },
				"&:hover": {
					border: dark
						? "1px solid rgba(255, 255, 255, 0.18)"
						: "1px solid rgba(15, 23, 42, 0.18)",
				},
				"&.Mui-focused": {
					border: dark
						? "1px solid #7aa2ff"
						: "1px solid #3b82f6",
					boxShadow: dark
						? "0 0 0 1px rgba(122, 162, 255, 0.15)"
						: "0 0 0 1px rgba(59, 130, 246, 0.12)",
					background: dark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
				},
				"& .MuiOutlinedInput-input": {
					padding: theme.spacing(1.3, 0),
					lineHeight: 1.4,
				},
				"& .MuiOutlinedInput-inputMarginDense": {
					padding: theme.spacing(1.3, 0),
					lineHeight: 1.4,
				},
				"& input": {
					color: dark ? "#f8fafc" : "#111827",
					fontSize: "0.98rem",
					"&::placeholder": {
						color: dark ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
						opacity: 1,
					},
				},
				"& .MuiInputAdornment-root": {
					marginLeft: theme.spacing(1),
					color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
				},
			},
			"& .MuiFormHelperText-root": {
				marginTop: theme.spacing(0.75),
				marginLeft: theme.spacing(0.5),
				fontSize: "0.78rem",
				transition: "color 180ms cubic-bezier(0.4, 0, 0.2, 1)",
			},
			"& .MuiInputAdornment-root": {
				transition: "color 180ms cubic-bezier(0.4, 0, 0.2, 1)",
			},
		},
		fadeSwap: {
			display: "inline-block",
			animation: "$contactFadeIn 180ms cubic-bezier(0.4, 0, 0.2, 1)",
		},
		"@keyframes contactFadeIn": {
			from: { opacity: 0, transform: "translateY(-2px)" },
			to: { opacity: 1, transform: "translateY(0)" },
		},
		inputError: {
			"& .MuiOutlinedInput-root": {
				border: `1px solid ${theme.palette.error.main}`,
				"&.Mui-focused": {
					border: `1px solid ${theme.palette.error.main}`,
					boxShadow: "0 0 0 1px rgba(244, 67, 54, 0.12)",
				},
			},
		},
		helperSuccess: {
			color: "#4caf50 !important",
		},
		helperWarning: {
			color: (dark ? "#fbbf24" : "#d97706") + " !important",
		},
		helperInfo: {
			color: (dark ? "#94a3b8" : "#64748b") + " !important",
		},
		extraRow: {
			display: "flex",
			alignItems: "stretch",
			gap: theme.spacing(2),
			width: "100%",
			"& > *": { minWidth: 0 },
			[theme.breakpoints.down("sm")]: { flexDirection: "column" },
		},
		expandableContent: {
			width: "100%",
			paddingTop: theme.spacing(1.75),
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1.5),
		},
		extraDeleteButton: {
			alignSelf: "center",
			flex: "0 0 auto",
			color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
			[theme.breakpoints.down("sm")]: { alignSelf: "flex-end" },
		},
		addInfoButton: {
			width: "100%",
			marginTop: theme.spacing(1),
			height: 44,
			borderRadius: 16,
			border: dark
				? "1px dashed rgba(255, 255, 255, 0.2)"
				: "1px dashed rgba(15, 23, 42, 0.2)",
			background: "transparent",
			color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
			fontSize: "0.92rem",
			fontWeight: 600,
			cursor: "pointer",
			transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
			fontFamily: "inherit",
			textTransform: "none",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			gap: 6,
			"&:hover": {
				background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
				borderColor: dark ? "#7aa2ff" : "#3b82f6",
				color: dark ? "#f8fafc" : "#111827",
			},
		},
		toggle: {
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			textTransform: "none",
			padding: theme.spacing(1.35, 1.8),
			borderRadius: 16,
			border: dark
				? "1px solid rgba(255, 255, 255, 0.08)"
				: "1px solid rgba(15, 23, 42, 0.08)",
			background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)",
			color: dark ? "#f8fafc" : "#111827",
			fontSize: "0.98rem",
			fontWeight: 500,
			cursor: "pointer",
			transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
			fontFamily: "inherit",
			"&:hover": {
				background: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.03)",
			},
		},
		toggleIcon: {
			color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
			transition: "transform 0.2s ease",
		},
		toggleIconOpen: { transform: "rotate(180deg)" },
		actions: {
			display: "flex",
			justifyContent: "flex-end",
			gap: 10,
			padding: theme.spacing(2, 3, 2.5),
			borderTop: dark
				? "1px solid rgba(255, 255, 255, 0.06)"
				: "1px solid rgba(15, 23, 42, 0.08)",
		},
		btnGhost: {
			height: 36,
			minWidth: 0,
			padding: "0 14px",
			borderRadius: 10,
			border: dark
				? "1px solid rgba(255, 255, 255, 0.12)"
				: "1px solid rgba(15, 23, 42, 0.12)",
			background: "transparent",
			color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
			fontSize: "0.875rem",
			fontWeight: 500,
			cursor: "pointer",
			transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
			fontFamily: "inherit",
			textTransform: "none",
			"&:hover:not(:disabled)": {
				background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
				color: dark ? "#f8fafc" : "#111827",
			},
			"&:disabled": { opacity: 0.5, cursor: "not-allowed" },
		},
		btnPrimary: {
			height: 36,
			minWidth: 0,
			padding: "0 14px",
			borderRadius: 10,
			border: "none",
			background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
			color: "#ffffff",
			fontSize: "0.875rem",
			fontWeight: 500,
			cursor: "pointer",
			transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
			fontFamily: "inherit",
			textTransform: "none",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			gap: 8,
			boxShadow: "none",
			position: "relative",
			"&:hover:not(:disabled)": {
				background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
			},
			"&:disabled": { opacity: 0.45, cursor: "not-allowed" },
		},
		btnProgress: {
			color: "#ffffff",
		},
	};
});

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("contactModal.formErrors.name.short"))
		// O tamanho da coluna: o nome montado pela triagem pode passar de 50.
		.max(255, i18n.t("contactModal.formErrors.name.long"))
		.required(i18n.t("contactModal.formErrors.name.required")),
	number: Yup.string()
		.min(12, "Número muito curto. Deve ter no mínimo 12 dígitos (55 + DDD + número)")
		.max(13, "Número muito longo. Deve ter no máximo 13 dígitos")
		.matches(/^55\d{10,11}$/, "Formato inválido. Deve começar com 55 seguido do DDD e número"),
	email: Yup.string().email(i18n.t("contactModal.formErrors.email.invalid")),
});

const ContactModal = ({
	open,
	onClose,
	contactId,
	initialValues,
	onSave,
	embedded = false,
	submitDisabled = false,
}) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = { name: "", number: "", email: "" };

	const [contact, setContact] = useState(initialState);
	const [validatingNumber, setValidatingNumber] = useState(false);
	const [numberValidation, setNumberValidation] = useState({
		valid: null,
		message: "",
		corrected: false,
		validation: null,
	});
	const setFieldValueRef = useRef(null);
	const [currentNumber, setCurrentNumber] = useState("");
	const [extraInfoOpen, setExtraInfoOpen] = useState(false);


	const validateWhatsAppNumber = async (number) => {
		if (!number || number.length < 12) {
			setNumberValidation({ valid: null, message: "", corrected: false, validation: null });
			return;
		}
		if (numberValidation.valid === true && numberValidation.correctNumber === number) return;

		setValidatingNumber(true);
		setNumberValidation({
			valid: null,
			message: "Validando número...",
			corrected: false,
			validation: null,
		});

		try {
			const { data } = await api.post("/contacts/validate", { number });
			if (!isMounted.current) return;

			// Só aceita a correção se ela for um telefone no formato do campo
			// (55 + DDD + número). Em 23/09/2026 o backend devolveu o LID do
			// WhatsApp como "número corrigido" e a tela trocou o número digitado.
			const isValidCorrection = /^55\d{10,11}$/.test(String(data.correctNumber || ""));
			if (data.valid === true && data.corrected && !isValidCorrection) {
				// Correção recusada: não mostra "número existe", para não parecer
				// confirmado. O backend corrigido nunca manda isso; é a segunda
				// proteção se o frontend subir antes do backend.
				setNumberValidation({
					valid: null,
					message: "Não foi possível confirmar este número agora. Confira antes de salvar.",
					corrected: false,
					correctNumber: number,
					validation: "unavailable",
				});
				return;
			}
			if (data.valid === true && data.corrected && isValidCorrection) {
				if (setFieldValueRef.current) {
					setFieldValueRef.current("number", data.correctNumber);
				}
				setCurrentNumber(data.correctNumber);
			}

			setNumberValidation({
				valid: data.valid,
				message: data.message || "",
				corrected: !!data.corrected && isValidCorrection,
				correctNumber: isValidCorrection ? data.correctNumber : number,
				validation: data.validation || null,
			});
		} catch (error) {
			if (!isMounted.current) return;
			console.error("Erro ao validar número:", error);
			setNumberValidation({
				valid: null,
				message: "Não foi possível verificar. Será validado no envio.",
				corrected: false,
				validation: "unavailable",
			});
		} finally {
			if (isMounted.current) setValidatingNumber(false);
		}
	};

	useEffect(() => {
		return () => { isMounted.current = false; };
	}, []);

	useEffect(() => {
		// Grupos têm "number" = JID digits (ex: 120363024...@g.us) que não passa
		// pelo endpoint /contacts/validate. Sem esse guard a modal dispara request
		// em vão toda vez que abre um ticket de grupo.
		if (contact.isGroup) {
			setNumberValidation({ valid: null, message: "", corrected: false, validation: null });
			return;
		}
		if (!currentNumber || currentNumber.length < 12) {
			setNumberValidation({ valid: null, message: "", corrected: false, validation: null });
			return;
		}
		const timer = setTimeout(() => validateWhatsAppNumber(currentNumber), 600);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentNumber, contact.isGroup]);

	useEffect(() => {
		const fetchContact = async () => {
			if (open && !contactId && !initialValues) {
				setCurrentNumber("");
				setNumberValidation({ valid: null, message: "", corrected: false });
				setExtraInfoOpen(false);
			}

			if (initialValues) {
				setContact((prev) => ({ ...prev, ...initialValues }));
				if (initialValues.number) setCurrentNumber(initialValues.number);
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact({ ...data, number: data.number });
					setCurrentNumber(data.number);
					setExtraInfoOpen(false);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		if (onClose) onClose();
		setContact(initialState);
		setCurrentNumber("");
		setNumberValidation({ valid: null, message: "", corrected: false });
		setExtraInfoOpen(false);
	};

	const handleSaveContact = async (values) => {
		try {
			const finalNumber = numberValidation.corrected && numberValidation.correctNumber
				? numberValidation.correctNumber
				: values.number;

			const finalValues = { ...values, number: finalNumber };

			if (contactId) {
				// Nome sem mudança não vai: o salvar não desfaz o nome que a triagem
				// montou enquanto o modal estava aberto, nem o muda de formato.
				const { name, ...withoutName } = finalValues;
				await api.put(`/contacts/${contactId}`, name === contact.name ? withoutName : finalValues);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", finalValues);
				let shouldClose = true;
				if (onSave) {
					const onSaveResult = await onSave(data, finalValues);
					if (onSaveResult === false) shouldClose = false;
				}
				if (shouldClose) handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (e) {
			toastError(e);
		}
	};

	const renderFormFields = (values, errors, touched) => (
		<>
			<div className={classes.section}>
				<div className={classes.sectionHeader}>
					<Typography className={classes.sectionTitle}>
						{i18n.t("contactModal.form.basicInfo")}
					</Typography>
					<div className={classes.sectionDivider} />
				</div>

				<div className={classes.fieldsRow}>
					<Field
						as={TextField}
						label={i18n.t("contactModal.form.name")}
						name="name"
						autoFocus
						error={touched.name && Boolean(errors.name)}
						helperText={touched.name && errors.name}
						variant="outlined"
						margin="dense"
						className={`${classes.field} ${classes.input} ${
							touched.name && Boolean(errors.name) ? classes.inputError : ""
						}`}
					/>

					<Field name="number">
						{({ field, form }) => {
							const hasSchemaError = touched.number && Boolean(errors.number);
							const isNotExists =
								numberValidation.valid === false &&
								numberValidation.validation === "waha" &&
								field.value.length >= 12;

							const isWahaOk =
								numberValidation.valid === true &&
								numberValidation.validation === "waha";

							const isMetaOnly =
								numberValidation.valid === true &&
								numberValidation.validation === "meta-format";

							const isUnavailable =
								numberValidation.validation === "unavailable" &&
								!validatingNumber;

							const helperText = hasSchemaError
								? errors.number
								: validatingNumber
								? "Validando número..."
								: isWahaOk
								? numberValidation.corrected
									? `✓ Número corrigido para ${formatPhoneDisplay(
										numberValidation.correctNumber
									)} · existe no WhatsApp`
									: "✓ Número existe no WhatsApp"
								: isMetaOnly
								? "Formato válido, mas não dá para confirmar se o número existe. Confira os dígitos."
								: isNotExists
								? numberValidation.message ||
									"Número não encontrado no WhatsApp. Verifique se está correto."
								: isUnavailable
								? "⚠ Não foi possível verificar. Será validado no envio."
								: "Digite o número. O código +55 será adicionado automaticamente";

							const helperClass = hasSchemaError
								? undefined
								: isWahaOk
								? classes.helperSuccess
								: isMetaOnly
								? classes.helperInfo
								: isUnavailable
								? classes.helperWarning
								: undefined;

							const iconKey = validatingNumber
								? "loading"
								: isWahaOk || isMetaOnly
								? "ok"
								: isNotExists
								? "error"
								: isUnavailable
								? "warn"
								: "none";

							const iconNode = validatingNumber ? (
								<CircularProgress size={20} />
							) : isWahaOk || isMetaOnly ? (
								<CheckCircle2 style={{ color: "#4caf50" }} />
							) : isNotExists ? (
								<AlertCircle style={{ color: "#f44336" }} />
							) : isUnavailable ? (
								<AlertCircle style={{ color: "#f59e0b" }} />
							) : null;

							return (
								<TextField
									label={i18n.t("contactModal.form.number")}
									{...field}
									value={formatPhoneDisplay(field.value)}
									onChange={(e) => {
										const input = e.target;
										const raw = input.value;
										const caret = input.selectionStart;
										const rawDigits = raw.replace(/\D/g, "");
										// Campo vazio e número inteiro de uma vez (colado ou
										// preenchido pelo navegador): o tamanho decide se o 55 da
										// frente é o país ou o DDD do RS (utils/phoneInput.js).
										const enteredWhole = !field.value && rawDigits.length >= 10;
										const sanitized = enteredWhole
											? sanitizePastedPhone(raw)
											: sanitizePhone(raw);
										form.setFieldValue("number", sanitized);
										setCurrentNumber(sanitized);

										// Correção no meio do número: o cursor fica onde a pessoa
										// estava, em vez de pular para o fim a cada reformatação.
										const caretAtEnd = caret === null || caret >= raw.length;
										if (!caretAtEnd && !enteredWhole) {
											const digitsBefore = digitsBeforeCaret(raw, caret);
											requestAnimationFrame(() => {
												const pos = caretAfterDigits(formatPhoneDisplay(sanitized), digitsBefore);
												try {
													input.setSelectionRange(pos, pos);
												} catch (err) {
													// campo saiu da tela; nada a fazer
												}
											});
										}
									}}
									error={hasSchemaError || isNotExists}
									helperText={
										<span key={helperText} className={classes.fadeSwap}>
											{helperText}
										</span>
									}
									placeholder="+55 (11) 99820-4015"
									variant="outlined"
									margin="dense"
									fullWidth
									className={`${classes.field} ${classes.input} ${
										hasSchemaError || isNotExists ? classes.inputError : ""
									}`}
									FormHelperTextProps={{ className: helperClass, component: "div" }}
									inputProps={{ inputMode: "numeric" }}
									InputProps={{
										endAdornment:
											field.value && field.value.length >= 12 && iconNode ? (
												<InputAdornment position="end">
													<span
														key={iconKey}
														className={classes.fadeSwap}
														style={{ display: "inline-flex" }}
													>
														{iconNode}
													</span>
												</InputAdornment>
											) : undefined,
									}}
								/>
							);
						}}
					</Field>
				</div>
			</div>

			{/* Só ao editar: ao adicionar, o contato nasce só com nome e número. */}
			{contactId && (
				<div className={classes.section}>
					<button
						type="button"
						className={classes.toggle}
						onClick={() => setExtraInfoOpen((current) => !current)}
					>
						<span>{i18n.t("contactModal.form.complementaryInfo")}</span>
						<ChevronDown
							className={`${classes.toggleIcon} ${
								extraInfoOpen ? classes.toggleIconOpen : ""
							}`}
						/>
					</button>

					<Collapse in={extraInfoOpen}>
						<div className={classes.expandableContent}>
							<Field
								as={TextField}
								label={i18n.t("contactModal.form.email")}
								name="email"
								error={touched.email && Boolean(errors.email)}
								helperText={touched.email && errors.email}
								placeholder="email@exemplo.com"
								fullWidth
								margin="dense"
								variant="outlined"
								className={`${classes.field} ${classes.input} ${
									touched.email && Boolean(errors.email) ? classes.inputError : ""
								}`}
							/>

							<FieldArray name="extraInfo">
								{({ push, remove }) => (
									<>
										{values.extraInfo &&
											values.extraInfo.length > 0 &&
											values.extraInfo.map((info, index) => (
												<div className={classes.extraRow} key={`${index}-info`}>
													<Field
														as={TextField}
														label={i18n.t("contactModal.form.extraName")}
														name={`extraInfo[${index}].name`}
														variant="outlined"
														margin="dense"
														className={`${classes.field} ${classes.input}`}
													/>
													<Field
														as={TextField}
														label={i18n.t("contactModal.form.extraValue")}
														name={`extraInfo[${index}].value`}
														variant="outlined"
														margin="dense"
														className={`${classes.field} ${classes.input}`}
													/>
													<IconButton
														size="small"
														className={classes.extraDeleteButton}
														onClick={() => remove(index)}
													>
														<Trash2 />
													</IconButton>
												</div>
											))}
										<button
											type="button"
											className={classes.addInfoButton}
											onClick={() => push({ name: "", value: "" })}
										>
											<Plus size={14} strokeWidth={2} />
											{i18n.t("contactModal.buttons.addExtraInfo")}
										</button>
									</>
								)}
							</FieldArray>
						</div>
					</Collapse>
				</div>
			)}
		</>
	);

	const renderActions = (isSubmitting, values) => {
		// Bloqueia só quando o WAHA confirmou que o número NÃO existe.
		// "meta-format" e "unavailable" não bloqueiam (validação final é no envio).
		const wahaConfirmedInvalid =
			numberValidation.valid === false &&
			numberValidation.validation === "waha";

		const isSaveDisabled =
			isSubmitting ||
			validatingNumber ||
			submitDisabled ||
			Boolean(values.number && values.number.length >= 12 && wahaConfirmedInvalid);

		return (
			<div className={classes.actions}>
				<button
					type="button"
					className={classes.btnGhost}
					onClick={handleClose}
					disabled={isSubmitting}
				>
					{i18n.t("contactModal.buttons.cancel")}
				</button>
				<button type="submit" className={classes.btnPrimary} disabled={isSaveDisabled}>
					{isSubmitting ? (
						<CircularProgress size={16} className={classes.btnProgress} />
					) : contactId ? (
						<Pencil size={15} strokeWidth={1.75} />
					) : (
						<UserPlus size={15} strokeWidth={1.75} />
					)}
					{contactId
						? i18n.t("contactModal.buttons.okEdit")
						: i18n.t("contactModal.buttons.okAdd")}
				</button>
			</div>
		);
	};

	const renderForm = () => (
		<Formik
			initialValues={contact}
			enableReinitialize={true}
			validationSchema={ContactSchema}
			validateOnChange={false}
			validateOnBlur={true}
			onSubmit={(values, actions) => {
				setTimeout(() => {
					handleSaveContact(values);
					actions.setSubmitting(false);
				}, 400);
			}}
		>
			{({ values, errors, touched, isSubmitting, setFieldValue }) => {
				setFieldValueRef.current = setFieldValue;
				return (
					<Form className={classes.embeddedForm}>
						{embedded ? (
							<div className={classes.content}>
								{renderFormFields(values, errors, touched)}
								{renderActions(isSubmitting, values)}
							</div>
						) : (
							<>
								<div className={classes.body}>
									<div className={classes.content}>
										{renderFormFields(values, errors, touched)}
									</div>
								</div>
								{renderActions(isSubmitting, values)}
							</>
						)}
					</Form>
				);
			}}
		</Formik>
	);

	if (embedded) {
		if (!open) return null;
		return <div className={classes.root}>{renderForm()}</div>;
	}

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth={false}
				scroll="paper"
				className={classes.dialog}
			>
				<div className={classes.header}>
					<Typography className={classes.title}>
						{contactId
							? i18n.t("contactModal.title.edit")
							: i18n.t("contactModal.title.add")}
					</Typography>
					<IconButton size="small" onClick={handleClose} className={classes.close}>
						<X size={18} />
					</IconButton>
				</div>
				{renderForm()}
			</Dialog>
		</div>
	);
};

export default ContactModal;
